import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { BeneficiaryDetail as Detail } from '@foundation/shared';
import { api } from '../lib/api';
import {
  BENEFICIARY_STATUS_TH,
  CATEGORY_TH,
  RISK_TH,
  ageFrom,
  formatDate,
  formatDateTime,
  formatNumber,
  formatSatang,
  fullName,
  maskNationalId,
} from '../lib/format';
import { Badge, ErrorAlert, Loading, Modal } from '../components/ui';
import { useAuth } from '../auth';

export default function BeneficiaryDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { me, allowed } = useAuth();
  const [b, setB] = useState<Detail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [showMember, setShowMember] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setB(await api<Detail>(`/beneficiaries/${id}`));
    } catch (e) {
      setError(e);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<void>, message: string) {
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(message);
      await load();
    } catch (e) {
      setError(e);
    }
  }

  if (error && !b) return <ErrorAlert error={error} />;
  if (!b) return <Loading />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{fullName(b)}</h1>
          <p>
            <span className="mono">{b.benCode}</span> ·{' '}
            <Badge value={b.status} label={BENEFICIARY_STATUS_TH[b.status] ?? b.status} />
            {b.riskLevel && (
              <>
                {' '}
                · ความเสี่ยง <Badge value={b.riskLevel} label={RISK_TH[b.riskLevel] ?? b.riskLevel} />
              </>
            )}
          </p>
        </div>
        <Link className="btn secondary" to="/beneficiaries">
          ← กลับรายการ
        </Link>
      </div>

      <ErrorAlert error={error} />
      {notice && <div className="alert success">{notice}</div>}

      {!b.consent.dataProcessingAt && (
        <div className="alert warn">
          ไม่พบบันทึกความยินยอมการเก็บข้อมูล — ตรวจสอบเอกสารและบันทึกให้ครบก่อนใช้ข้อมูลนี้
        </div>
      )}

      <div className="grid grid-2">
        <div className="card">
          <h2>ข้อมูลส่วนบุคคล</h2>
          <dl className="detail-list">
            <dt>เลขบัตรประชาชน</dt>
            <dd>
              <span className="mono">{revealed ?? maskNationalId(b.nationalIdLast4)}</span>
              {b.nationalIdLast4 && me?.role === 'super_admin' && !revealed && (
                <>
                  {' '}
                  <button
                    className="link"
                    onClick={() => {
                      const reason = prompt(
                        'ระบุเหตุผลในการเปิดดูเลขบัตรประชาชน (บันทึกลง audit log)',
                      );
                      if (!reason || reason.length < 5) return;
                      void act(async () => {
                        const res = await api<{ nationalId: string | null }>(
                          `/beneficiaries/${b.id}/national-id?reason=${encodeURIComponent(reason)}`,
                        );
                        setRevealed(res.nationalId);
                      }, 'เปิดดูเลขบัตรแล้ว — บันทึกการเข้าถึงเรียบร้อย');
                    }}
                  >
                    แสดงเลขเต็ม
                  </button>
                </>
              )}
            </dd>
            <dt>ชื่อเล่น</dt>
            <dd>{b.nickname ?? '—'}</dd>
            <dt>วันเกิด / อายุ</dt>
            <dd>
              {formatDate(b.birthDate)} {b.birthDate && `(${ageFrom(b.birthDate)})`}
            </dd>
            <dt>เบอร์โทร</dt>
            <dd>
              {b.phone ?? '—'}
              {b.phoneAlt && ` / ${b.phoneAlt}`}
            </dd>
            <dt>ประเภท</dt>
            <dd>{b.category ? (CATEGORY_TH[b.category] ?? b.category) : '—'}</dd>
          </dl>
        </div>

        <div className="card">
          <h2>ที่อยู่และการดูแล</h2>
          <dl className="detail-list">
            <dt>ที่อยู่</dt>
            <dd>{b.addressLine ?? '—'}</dd>
            <dt>ตำบล/พื้นที่</dt>
            <dd>{b.areaName ?? '—'}</dd>
            <dt>พิกัดบ้าน</dt>
            <dd>
              {b.lat !== null && b.lng !== null ? (
                <a
                  href={`https://www.google.com/maps?q=${b.lat},${b.lng}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {b.lat.toFixed(5)}, {b.lng.toFixed(5)}
                </a>
              ) : (
                <span className="muted">ยังไม่ได้ปักหมุด — บันทึกตอนลงพื้นที่ครั้งแรก</span>
              )}
            </dd>
            <dt>เจ้าหน้าที่ผู้ดูแล</dt>
            <dd>{b.primaryOfficerName ?? <span className="badge amber">ยังไม่มีผู้ดูแล</span>}</dd>
            <dt>จำนวนคนในครัวเรือน</dt>
            <dd>{formatNumber(b.householdSize)}</dd>
            <dt>รายได้ครัวเรือน/เดือน</dt>
            <dd>{formatSatang(b.monthlyIncome)}</dd>
            <dt>วันแรกรับ</dt>
            <dd>{formatDate(b.intakeDate)}</dd>
          </dl>
        </div>
      </div>

      <div className="card">
        <h2>
          สมาชิกในครัวเรือน{' '}
          <span className="muted small">({b.householdMembers.length} คน)</span>
        </h2>
        {b.householdMembers.length === 0 ? (
          <p className="muted small">ยังไม่ได้บันทึกสมาชิกในครัวเรือน</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="wrap">ชื่อ-สกุล</th>
                  <th>ความสัมพันธ์</th>
                  <th>อายุ</th>
                  <th>อาชีพ</th>
                  <th>ผู้พึ่งพิง</th>
                  <th className="wrap">ข้อมูลสุขภาพ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {b.householdMembers.map((m) => (
                  <tr key={m.id}>
                    <td className="wrap">{m.fullName}</td>
                    <td>{m.relation ?? '—'}</td>
                    <td>{ageFrom(m.birthDate)}</td>
                    <td>{m.occupation ?? '—'}</td>
                    <td>{m.isDependent ? 'ใช่' : 'ไม่'}</td>
                    <td className="wrap">
                      {'healthNote' in m ? (
                        (m.healthNote ?? '—')
                      ) : (
                        <span className="muted small">ไม่มีสิทธิ์ดู</span>
                      )}
                    </td>
                    <td>
                      {allowed('beneficiary:write') && (
                        <button
                          className="link"
                          onClick={() => {
                            if (!confirm(`ลบ ${m.fullName} ออกจากรายชื่อสมาชิกครัวเรือน?`)) return;
                            void act(
                              () =>
                                api(`/beneficiaries/${b.id}/household/${m.id}`, {
                                  method: 'DELETE',
                                }),
                              'ลบสมาชิกครัวเรือนแล้ว',
                            );
                          }}
                        >
                          ลบ
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {allowed('beneficiary:write') && (
          <button className="secondary" style={{ marginTop: 12 }} onClick={() => setShowMember(true)}>
            + เพิ่มสมาชิกครัวเรือน
          </button>
        )}
      </div>

      <div className="grid grid-2">
        <div className="card">
          <h2>ความยินยอม (PDPA)</h2>
          <dl className="detail-list">
            <dt>เก็บและใช้ข้อมูล</dt>
            <dd>
              {b.consent.dataProcessingAt ? (
                <span className="badge green">ยินยอม {formatDate(b.consent.dataProcessingAt)}</span>
              ) : (
                <span className="badge red">ไม่พบบันทึก</span>
              )}
            </dd>
            <dt>เผยแพร่ภาพ</dt>
            <dd>
              {b.consent.photoPublishAt ? (
                <span className="badge green">ยินยอม</span>
              ) : (
                <span className="badge amber">ไม่ยินยอม — ห้ามใช้ภาพประชาสัมพันธ์</span>
              )}
            </dd>
            <dt>เวอร์ชันนโยบาย</dt>
            <dd className="mono">{b.consent.version ?? '—'}</dd>
          </dl>
        </div>

        <div className="card">
          <h2>สถานะเคส</h2>
          {allowed('beneficiary:write') ? (
            <div className="field" style={{ maxWidth: 280 }}>
              <label htmlFor="statusSel">เปลี่ยนสถานะ</label>
              <select
                id="statusSel"
                value={b.status}
                onChange={(e) => {
                  const next = e.target.value;
                  const closing = ['closed', 'graduated', 'rejected', 'deceased'].includes(next);
                  const reason = closing ? prompt('เหตุผลในการปิดเคส') : null;
                  if (closing && !reason) return;
                  void act(
                    () =>
                      api(`/beneficiaries/${b.id}`, {
                        method: 'PATCH',
                        body: { status: next, closedReason: reason ?? undefined },
                      }),
                    'อัปเดตสถานะแล้ว',
                  );
                }}
              >
                {Object.entries(BENEFICIARY_STATUS_TH).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <p className="muted small">คุณไม่มีสิทธิ์แก้ไขสถานะ</p>
          )}
          {b.closedAt && (
            <dl className="detail-list" style={{ marginTop: 10 }}>
              <dt>ปิดเคสเมื่อ</dt>
              <dd>{formatDateTime(b.closedAt)}</dd>
              <dt>เหตุผล</dt>
              <dd>{b.closedReason ?? '—'}</dd>
            </dl>
          )}
          {b.notes && (
            <>
              <h2 style={{ marginTop: 18 }}>บันทึกเพิ่มเติม</h2>
              <p className="small" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                {b.notes}
              </p>
            </>
          )}
        </div>
      </div>

      {allowed('beneficiary:delete') && (
        <div className="card">
          <h2>ลบข้อมูล</h2>
          <p className="muted small" style={{ marginTop: 0 }}>
            ระบบใช้การลบแบบซ่อน (soft delete) ข้อมูลยังอยู่ในฐานข้อมูลเพื่อการตรวจสอบ
            หากต้องการลบตามคำขอ PDPA ให้ใช้ฟังก์ชันลบตัวตน (เฟส 4)
          </p>
          <button
            className="danger"
            onClick={() => {
              const reason = prompt('เหตุผลในการลบ (อย่างน้อย 5 ตัวอักษร)');
              if (!reason || reason.length < 5) return;
              void (async () => {
                try {
                  await api(`/beneficiaries/${b.id}?reason=${encodeURIComponent(reason)}`, {
                    method: 'DELETE',
                  });
                  navigate('/beneficiaries');
                } catch (e) {
                  setError(e);
                }
              })();
            }}
          >
            ลบข้อมูลผู้รับความช่วยเหลือ
          </button>
        </div>
      )}

      {showMember && (
        <AddMemberModal
          beneficiaryId={b.id}
          canRecordHealth={allowed('beneficiary:read_sensitive')}
          onClose={() => setShowMember(false)}
          onSaved={() => {
            setShowMember(false);
            void load();
          }}
        />
      )}
    </>
  );
}

function AddMemberModal({
  beneficiaryId,
  canRecordHealth,
  onClose,
  onSaved,
}: {
  beneficiaryId: string;
  canRecordHealth: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    fullName: '',
    relation: '',
    birthDate: '',
    occupation: '',
    healthNote: '',
    isDependent: true,
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api(`/beneficiaries/${beneficiaryId}/household`, {
        method: 'POST',
        body: {
          fullName: form.fullName.trim(),
          relation: form.relation.trim() || undefined,
          birthDate: form.birthDate || undefined,
          occupation: form.occupation.trim() || undefined,
          healthNote: form.healthNote.trim() || undefined,
          isDependent: form.isDependent,
        },
      });
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="เพิ่มสมาชิกครัวเรือน" onClose={onClose}>
      <form onSubmit={submit}>
        <ErrorAlert error={error} />
        <div className="form-row">
          <div className="field">
            <label htmlFor="mName">ชื่อ-สกุล *</label>
            <input
              id="mName"
              required
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="mRel">ความสัมพันธ์</label>
            <input
              id="mRel"
              value={form.relation}
              onChange={(e) => setForm({ ...form, relation: e.target.value })}
              placeholder="บุตร / คู่สมรส / บิดา"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="field">
            <label htmlFor="mBirth">วันเกิด</label>
            <input
              id="mBirth"
              type="date"
              value={form.birthDate}
              onChange={(e) => setForm({ ...form, birthDate: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="mOcc">อาชีพ</label>
            <input
              id="mOcc"
              value={form.occupation}
              onChange={(e) => setForm({ ...form, occupation: e.target.value })}
            />
          </div>
        </div>

        {canRecordHealth && (
          <div className="field">
            <label htmlFor="mHealth">ข้อมูลสุขภาพ</label>
            <input
              id="mHealth"
              value={form.healthNote}
              onChange={(e) => setForm({ ...form, healthNote: e.target.value })}
            />
            <div className="hint">
              ข้อมูลอ่อนไหวตาม PDPA ม.26 — บันทึกเมื่อได้รับความยินยอมแล้วเท่านั้น
            </div>
          </div>
        )}

        <label className="checkbox" style={{ marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={form.isDependent}
            onChange={(e) => setForm({ ...form, isDependent: e.target.checked })}
          />
          <span>เป็นผู้พึ่งพิง (ไม่มีรายได้ของตนเอง)</span>
        </label>

        <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="secondary" onClick={onClose}>
            ยกเลิก
          </button>
          <button type="submit" disabled={busy}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
